import { useEffect, type ReactNode, type Ref } from "react";
import { toast } from "sonner";

import type { AtlasSearchHandle } from "@/components/shell/CommandPalette";

import { ChatLauncher } from "@/components/appshell/ChatLauncher";
import { LeftBar } from "@/components/appshell/LeftBar";
import { RightBar } from "@/components/appshell/RightBar";
import { SettingsOverlay } from "@/components/appshell/settings/SettingsOverlay";
import { BillingPanel } from "@/components/appshell/billing/BillingPanel";
import { TopBar } from "@/components/appshell/TopBar";
import { AppErrorBoundary } from "@/components/feedback";
import { Dialog, DialogContent } from "@/components/ui/overlays";
import { keys } from "@/hooks/queries";
import { queryClient } from "@/lib/api";
import { useShell } from "@/stores/shell";
import { useToolsOverlay } from "@/stores/tools-overlay";

/**
 * Reads the Stripe redirect on load and surfaces it as a toast + opens
 * billing — there's no router to catch `/billing`, `/billing/success`,
 * `/billing/cancel` as real routes (see `billing/service.py`'s
 * success_url/cancel_url/return_url). Also accepts a generic `?checkout=`
 * query param for forward-compatibility.
 *
 * KNOWN GAP (pre-existing, server-side, out of scope here — `server/**` is
 * off-limits for this task): the backend mounts the built studio via plain
 * `StaticFiles(html=True)` with no SPA catch-all, so a direct browser
 * navigation to `/billing/success` 404s before this code ever runs. This
 * handler is correct and forward-compatible, but only actually fires once
 * that gap is closed (a 404→index.html fallback, or success/cancel URLs that
 * carry a query param on `/` instead of a path) — already flagged in
 * docs/architecture/frontend/app-shell-and-bundles.md.
 */
function useBillingRedirect(): void {
  useEffect(() => {
    const { pathname, search, hash } = window.location;
    const params = new URLSearchParams(search);
    const checkoutParam = params.get("checkout");
    let handled = false;

    if (pathname === "/billing/success" || checkoutParam === "success") {
      toast.success("You're upgraded", { description: "Your billing add-ons are now active." });
      handled = true;
    } else if (pathname === "/billing/cancel" || checkoutParam === "cancel") {
      toast("Checkout canceled", { description: "No changes were made to your plan." });
      handled = true;
    } else if (pathname === "/billing" || params.get("billing") === "return") {
      // Returned from the Stripe customer portal — the subscription may have changed.
      handled = true;
    }

    if (handled) {
      void queryClient.invalidateQueries({ queryKey: keys.billing });
      useShell.getState().openBilling();
      window.history.replaceState(null, "", "/" + hash);
    }
    // Once, on mount — this is a one-time landing check, not a live route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * The obsidian app shell: LeftBar | Center | RightBar, wrapping a collapsed
 * TopBar and a collapsed RightBar (small edge open-buttons), a borderless
 * center, a pop-out chat launcher, and the Settings/Billing overlays. The
 * CENTER is whatever the caller passes as `children` — today the always-
 * mounted Dockview `WorkspaceSurface` (the dock IS the app now; the old
 * per-kind `Workspace` tab-content switcher in `App.tsx` is gone). This
 * component owns none of that logic, only the chrome around it.
 */
export function AppShell({
  children,
  onAddWebsite,
  onSmartExport,
  onManageTokens,
  onManageBackups,
  searchRef,
}: {
  children: ReactNode;
  onAddWebsite: () => void;
  onSmartExport: () => void;
  onManageTokens: () => void;
  onManageBackups: () => void;
  /** Shared down to the TopBar search combobox so ⌘K / the LeftBar can open it. */
  searchRef?: Ref<AtlasSearchHandle>;
}) {
  const billingOpen = useShell((s) => s.billingOpen);
  const fullscreen = useShell((s) => s.fullscreen);
  const leftBarOpen = useShell((s) => s.leftBarOpen);
  useBillingRedirect();

  const toggleTools = useToolsOverlay((s) => s.toggleTools);

  // The shell's keyboard map, in one place. Every binding here is ALSO printed
  // on the control it drives and listed in the `?` sheet — a shortcut nobody can
  // discover is a shortcut for the person who wrote it.
  //
  // `[` and `]` for the side panels are near-universal (Notion, Linear, VS Code);
  // `\` continues the run for the top bar. None of them fire while the caret is
  // in a field, so typing a bracket into a note still types a bracket.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.tagName === "SELECT" ||
          el.isContentEditable)
      ) {
        return;
      }
      const shell = useShell.getState();
      switch (e.key) {
        case " ":
          e.preventDefault();
          toggleTools();
          return;
        case "[":
          e.preventDefault();
          shell.toggleLeftBar();
          return;
        case "]":
          e.preventDefault();
          shell.toggleRightBar();
          return;
        case "\\":
          e.preventDefault();
          shell.toggleTopBar();
          return;
        case "?":
          e.preventDefault();
          shell.toggleShortcuts();
          return;
        default:
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [toggleTools]);

  // Fullscreen = the opened content as a borderless sheet: only the LeftBar's
  // collapsed spine stays. EXPANDING the LeftBar while fullscreen re-reveals
  // the top-bar affordance (its Show-top-bar button) and the RightBar spine —
  // so the chrome is one click away, never in the way.
  const chrome = !fullscreen || leftBarOpen;

  return (
    <AppErrorBoundary label="shell">
      <div className="flex h-full min-h-0 flex-1 flex-col bg-background text-foreground">
        {chrome && (
          <TopBar
            onAddWebsite={onAddWebsite}
            onSmartExport={onSmartExport}
            onManageTokens={onManageTokens}
            onManageBackups={onManageBackups}
            searchRef={searchRef}
          />
        )}
        <div className="flex min-h-0 flex-1">
          {/* Named landmarks, so a screen reader can list the regions from the
              rotor and jump into one without tabbing through the document.
              Without them every panel was an unnamed <div> and the toggles
              announced as a bare "button". */}
          <LeftBar onAddWebsite={onAddWebsite} />
          <main
            id="gv-workspace"
            aria-label="Workspace"
            className="relative flex min-h-0 min-w-0 flex-1 flex-col"
          >
            {children}
            {chrome && <ChatLauncher />}
          </main>
          {chrome && <RightBar />}
        </div>
      </div>

      {/* The Tools overlay, the shortcut sheet, the note chooser and the file
          picker are NOT here any more: they are product surfaces, not desktop
          chrome, and this component only renders on desktop. `GlobalOverlays`
          mounts them once above the layout branch, so a phone and a tablet get
          the same ones. See components/shell/GlobalOverlays.tsx. */}
      <SettingsOverlay />

      <Dialog
        open={billingOpen}
        onOpenChange={(v) => (v ? useShell.getState().openBilling() : useShell.getState().closeBilling())}
      >
        <DialogContent title="Billing" className="w-[min(480px,92vw)]">
          <BillingPanel />
        </DialogContent>
      </Dialog>
    </AppErrorBoundary>
  );
}
